/**
 * Quiz progressive promocode helpers (10% → 15% → 20%).
 */

/**
 * Map quiz profile completion (0–100) to unlocked discount tier.
 * Thresholds align with demo progress bar thirds.
 *
 * @param {number} profilePct
 * @returns {10|15|20}
 */
export function resolvePromoTier(profilePct) {
  const p = Number(profilePct);
  const pct = Number.isFinite(p) ? Math.min(100, Math.max(0, p)) : 0;
  if (pct >= 67) return 20;
  if (pct >= 34) return 15;
  return 10;
}

/**
 * @param {10|15|20} tier
 * @param {Record<number|string, string>} codesMap
 * @returns {string|null}
 */
/**
 * True when the promocode's allowed ticket ids include the recommended ticket.
 *
 * @param {Array<string|number>} ticketIds
 * @param {Array<string|number>} allowedIds
 * @returns {boolean}
 */
export function promoCoversTicket(ticketIds, allowedIds) {
  const allowed = (allowedIds || []).map((id) => String(id));
  return (ticketIds || []).some((id) => id != null && allowed.includes(String(id)));
}

export function getPromoCode(tier, codesMap) {
  if (!codesMap) return null;
  const code = codesMap[tier] != null ? codesMap[tier] : codesMap[String(tier)];
  if (code == null || String(code).trim() === '') return null;
  return String(code).trim();
}

/**
 * Validate a promocode via the site API (same contract as TicketsPromocode).
 *
 * @param {string} code
 * @param {{ site?: string, fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<{ ok: boolean, status: number, data: object|null }>}
 */
export function validatePromo(code, opts) {
  opts = opts || {};
  const promocode = String(code || '').trim();
  if (!promocode) {
    return Promise.resolve({ ok: false, status: 0, data: null });
  }

  const fetchImpl = opts.fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!fetchImpl) {
    return Promise.reject(new Error('fetch_unavailable'));
  }

  const params = new URLSearchParams({ promocode: promocode });
  if (opts.site) params.set('site', opts.site);

  return fetchImpl('/api/promocode?' + params.toString(), {
    headers: { Accept: 'application/json' },
  }).then(function (res) {
    return res.json().then(function (data) {
      return { res: res, data: data };
    }).catch(function () {
      return { res: res, data: null };
    });
  }).then(function (result) {
    const res = result.res;
    const data = result.data;
    const ok = res.ok && data && Array.isArray(data.tickets) && data.tickets.length > 0;
    return { ok: ok, status: res.status, data: data };
  });
}
